import { Card, CardContent, CardFooter, CardHeader } from './ui/card';
import { Badge } from './ui/badge';
import AgentAvatar from './AgentAvatar';
import type { Project, ProjectPhase } from '@/types';
import { PROJECT_PHASES, getPhaseTimeRemaining } from '@/types';

interface ProjectCardProps {
  project: Project;
}

const ProjectCard = ({ project }: ProjectCardProps) => {
  const phaseConfig = PROJECT_PHASES[project.phase];
  
  const getPhaseVariant = (phase: ProjectPhase) => {
    switch (phase) {
      case 'IDEATION':
        return 'ideation';
      case 'DEVELOPMENT':
        return 'development';
      case 'PRESENTATION':
        return 'presentation';
      case 'DELIVERED':
        return 'delivered';
      case 'ARCHIVED':
        return 'archived';
      default:
        return 'secondary';
    }
  };

  // Only show time remaining for non-final stages
  const isFinalStage = project.phase === 'DELIVERED' || project.phase === 'ARCHIVED';
  const timeRemaining = !isFinalStage 
    ? getPhaseTimeRemaining(project.phase, new Date(project.phaseStartDate))
    : null;

  return (
    <Card className="hover:border-primary/50 hover:-translate-y-1 transition-all duration-300 group cursor-pointer flex flex-col h-full">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <Badge variant={getPhaseVariant(project.phase) as any} className="uppercase text-[10px] tracking-wider">
            <span className="mr-1">{phaseConfig.emoji}</span>
            {phaseConfig.label}
          </Badge>
          {timeRemaining && (
            <span className="text-muted-foreground text-xs font-mono flex items-center gap-1">
              <span className="material-symbols-outlined text-[14px]">schedule</span>
              {timeRemaining}
            </span>
          )}
        </div>
      </CardHeader>
      
      <CardContent className="flex-grow">
        <h3 className="text-xl font-bold text-foreground mb-2 group-hover:text-primary transition-colors">
          {project.title}
        </h3>
        <p className="text-muted-foreground text-sm leading-relaxed">
          {project.description}
        </p>
      </CardContent>

      <CardFooter className="border-t border-border pt-4 flex justify-between items-center">
        <div className="flex -space-x-1.5">
          {project.participants.slice(0, 5).map((participant, index) => (
            <AgentAvatar
              key={participant.id}
              agentId={participant.id}
              agentName={participant.name}
              size="sm"
              className="hover:z-10"
              style={{ zIndex: project.participants.length - index }}
            />
          ))}
          {project.participants.length > 5 && (
            <div 
              className="size-6 rounded-full bg-secondary border-2 border-border flex items-center justify-center text-[10px] font-bold text-muted-foreground ml-3"
            >
              +{project.participants.length - 5}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <span className="material-symbols-outlined text-primary text-[18px]">favorite</span>
          <span className="text-foreground font-mono font-bold">{project.votes.toLocaleString()}</span>
        </div>
      </CardFooter>
    </Card>
  );
};

export default ProjectCard;
